// Iter-231 — Global client-side error capture (Admin Roadmap §1.9).
//
// Installs two handlers:
//   • window.onerror — synchronous exceptions thrown during render or
//     event handlers (anything React doesn't catch via ErrorBoundary).
//   • window.onunhandledrejection — Promise rejections that never got
//     a `.catch()` (most async-await bugs).
//
// Both POST to `/api/client-errors` which dedups by stack-hash, so a
// runaway page that throws 200x in a single session adds ZERO row
// pressure to the collection.
//
// Self-throttling: a single in-memory rate-limiter caps outbound posts
// at 1/sec to protect the backend from a bad page that throws
// thousands of times per second. The backend has its own rate-limit
// as a second layer.

import { reportClientError } from "./api";

let _last_sent_at = 0;
const _THROTTLE_MS = 1000;

function shouldThrottle() {
  const now = Date.now();
  if (now - _last_sent_at < _THROTTLE_MS) return true;
  _last_sent_at = now;
  return false;
}

function safeReport(payload) {
  if (shouldThrottle()) return;
  try {
    reportClientError(payload);
  } catch {
    /* swallow — never let error reporting throw */
  }
}

let _installed = false;
export function installErrorCapture() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;

  window.addEventListener("error", (event) => {
    if (!event || !event.message) return;
    safeReport({
      message: String(event.message).slice(0, 500),
      stack: event.error?.stack ? String(event.error.stack).slice(0, 4000) : null,
      url: window.location?.href || null,
      user_agent: navigator?.userAgent || null,
      line: event.lineno ?? null,
      column: event.colno ?? null,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    if (!reason) return;
    const message = typeof reason === "string"
      ? reason
      : (reason?.message || String(reason));
    safeReport({
      message: String(message).slice(0, 500),
      stack: reason?.stack ? String(reason.stack).slice(0, 4000) : null,
      url: window.location?.href || null,
      user_agent: navigator?.userAgent || null,
    });
  });
}
